'use client';

import { type FC, useContext, useEffect, useMemo, useState } from 'react';
import styles from './LeftNavAdmin.module.css';
import type { LeftNavAdminProps } from './LeftNavAdmin.types';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { Flex } from '@/_components';
import { LeftNavItem } from '@/_components/LeftNavItem';
import { LayoutContext } from '@/_contexts/LayoutProvider';
import { getClasses } from '@/_utils/classnames';
import { usePathname, useParams } from 'next/navigation';
import { SelectedEntity } from './components/SelectedEntity';
import { SelectedClient } from './components/SelectedClient';
import { AdminEntityNav } from './components/AdminEntityNav/AdminEntityNav';
import { ClientDashboardNav } from './components/ClientDashboardNav/ClientDashboardNav';
import { SettingsNav } from './components/SettingsNav/SettingsNav';
import { DefaultAdvisorNav } from './components/DefaultAdvisorNav/DefaultAdvisorNav';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { DropLinksContext } from '@/_contexts/DropLinksProvider';
import { getCompany } from '@/_api/client-companies';
import { Company } from '@/_types/models/company';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';
import { InsightsNav } from './components/InsightsNav';
import { DeveloperNav } from './components/DeveloperNav';

export const LeftNavAdmin: FC<LeftNavAdminProps> = () => {
  const { isCentered, isPublic } = useContext(LayoutContext);
  const { preferences } = useContext(FirmSettingsContext);
  const { selectedCompany } = useContext(CompanyContext);
  const { unseenDropLinkUploadsCount } = useContext(DropLinksContext);
  const { permissions, isAdvisor, token } = useContext(UserContext);
  const path = usePathname();
  const { companyId, questionnaireId } = useParams();

  const [isHovering, setHovering] = useState<boolean>(false);
  const [localCompany, setLocalCompany] = useState<Company | undefined>(
    undefined,
  );

  const asideClasses: string = getClasses(
    styles.leftNavAdmin,
    'flex gap-4 flex-column',
  );

  const isAdminQuestionnairePreview: boolean =
    isAdvisor &&
    path.startsWith('/preview/') &&
    typeof companyId === 'string' &&
    typeof questionnaireId === 'string';
  const isAdminDashboard: boolean =
    !!selectedCompany?.id && path.startsWith('/admin/');
  const isClientDashboard: boolean = path.startsWith('/clients/');
  const isFirmSettings: boolean = path.startsWith('/settings');
  const isInsights: boolean = path.startsWith('/insights');
  const isDeveloper: boolean = path.startsWith('/developer');

  const hasAdminDashboardBottomItems: boolean = useMemo(() => {
    return (
      !isClientDashboard &&
      !isAdminDashboard &&
      !isAdminQuestionnairePreview &&
      !isFirmSettings
    );
  }, [
    isClientDashboard,
    isAdminDashboard,
    isAdminQuestionnairePreview,
    isFirmSettings,
  ]);

  const hasFirmSettingsPermission: boolean = getHasPermission(
    PermissionsMapEnum['firm.settings'],
    permissions,
  );

  useEffect(() => {
    if (!isAdminQuestionnairePreview || !token || !companyId) return;

    const _getCompanyFromId = async () => {
      try {
        const company = await getCompany({
          companyId: parseInt(companyId?.toString()),
          token,
        });

        if (company?.hash_id) {
          setLocalCompany(company);
        }
      } catch (error) {
        console.error(error);
      }
    };

    _getCompanyFromId();
  }, [isAdminQuestionnairePreview, companyId, token]);

  if (isCentered || isPublic) return null;

  const isProfileSettings: boolean = path.startsWith('/admin-profile');
  const isWorkflowsPage: boolean = path.startsWith('/workflows');
  const isDropLinks: boolean = path.startsWith('/admin-drop-links');

  const onMouseEnter = () => setHovering(true);
  const onMouseLeave = () => setHovering(false);

  return (
    <aside
      className={asideClasses}
      data-hide-on-print="true"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Flex isColumn={true} gap={4}>
        {(isAdminDashboard || isAdminQuestionnairePreview) && (
          <>
            <SelectedEntity company={selectedCompany || localCompany} />
            <AdminEntityNav company={selectedCompany || localCompany} />
          </>
        )}
        {isClientDashboard && (
          <>
            <SelectedClient />
            <ClientDashboardNav />
          </>
        )}
        {isFirmSettings && <SettingsNav isHovering={isHovering} />}
        {isInsights && <InsightsNav isHovering={isHovering} />}
        {isDeveloper && <DeveloperNav isHovering={isHovering} />}
        {!isAdminDashboard &&
          !isAdminQuestionnairePreview &&
          !isClientDashboard &&
          !isFirmSettings &&
          !isInsights &&
          !isDeveloper && <DefaultAdvisorNav />}
      </Flex>

      {hasAdminDashboardBottomItems && (
        <Flex isColumn={true} gap={4}>
          {preferences?.drop_link_enabled && (
            <LeftNavItem
              name="Drop Link"
              icon="tray"
              href="/admin-drop-links"
              notificationCount={unseenDropLinkUploadsCount}
              isActive={isDropLinks}
            />
          )}
          {preferences?.workflow_automation_enabled && (
            <LeftNavItem
              name="Workflows"
              isActive={isWorkflowsPage}
              icon="flow-arrow"
              href="/workflows"
            />
          )}
          {hasFirmSettingsPermission && (
            <LeftNavItem
              name="Firm Settings"
              isActive={isFirmSettings}
              icon="gear"
              href="/settings"
            />
          )}
          <LeftNavItem
            name="Profile Settings"
            isActive={isProfileSettings}
            icon="user-circle-gear"
            href="/admin-profile"
          />
        </Flex>
      )}
    </aside>
  );
};
